import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const EMPTY = { name: "", slug: "", description: "", image: "", order: 0 };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Kategoriyi silmek istiyor musunuz?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Silindi"); load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl">Kategoriler</h1>
          <p className="text-larc-muted mt-1">Ürün kategorilerini yönetin.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold transition-colors" data-testid="add-category">
          <Plus size={16} /> Yeni Kategori
        </button>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-larc-border rounded-2xl overflow-hidden">
            {c.image && <div className="aspect-[16/9] bg-larc-alt"><img src={c.image} alt="" className="w-full h-full object-cover" /></div>}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-xl">{c.name}</div>
                  <div className="text-xs text-larc-muted mt-0.5">/{c.slug}</div>
                </div>
                <div className="text-xs text-larc-muted">Sıra: {c.order ?? 0}</div>
              </div>
              {c.description && <p className="text-sm text-larc-muted mt-3 line-clamp-2">{c.description}</p>}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={() => setEditing(c)} className="inline-flex items-center gap-1.5 text-sm text-larc-muted hover:text-larc-ink" data-testid={`cat-edit-${c.slug}`}><Pencil size={14} /> Düzenle</button>
                <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1.5 text-sm text-larc-muted hover:text-red-600"><Trash2 size={14} /> Sil</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <CatForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CatForm({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { ...form, order: Number(form.order) || 0 };
    try {
      if (isNew) await api.post("/admin/categories", body);
      else await api.put(`/admin/categories/${initial.id}`, body);
      toast.success("Kaydedildi"); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-larc-border">
          <h3 className="font-heading text-2xl">{isNew ? "Yeni Kategori" : "Kategoriyi Düzenle"}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <Field label="Ad" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cat-name" />
          <Field label="Slug" required value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="cat-slug" />
          <Field label="Görsel URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          <Field label="Sıra" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          <Field label="Açıklama" textarea value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-full border border-larc-border text-sm">İptal</button>
            <button disabled={saving} className="px-6 py-2.5 rounded-full bg-larc-ink text-white text-sm">{saving ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", textarea, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}{required && " *"}</span>
      {textarea ? (
        <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" data-testid={testid} />
      ) : (
        <input required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" data-testid={testid} />
      )}
    </label>
  );
}
